import React from "react";
import { Controller, useFieldArray } from "react-hook-form";
import Typography from "components/typography";
import Icon from "components/icon";
import Select from "components/select/select";
import TripLocationInput from "./TripLocationInput";
import AdditionalCostRow from "./AdditionalCostRow";
import { LocationPairRoute } from "_interfaces/driverTripCalculator.interfaces";
import { formatTime } from "_services/modules/driverTripCalculator/calculateTrip";
import { useAppTranslation } from "locale/useAppTranslation";

export interface SegmentSectionProps {
  index: number;
  segmentColor: string;
  route?: { totalDistance: number; totalTime: number; pairs?: LocationPairRoute[] };
  control: any;
  watch: any;
  setValue: any;
  errors: any;
  tripTypeOptions: { value: string; label: string }[];
  showFreezerIdleTime: boolean;
  watchedEnableAdditionalCost: boolean;
  canRemove: boolean;
  onRemove: () => void;
  onRouteUpdate: (distance: number, time: number, pairs: LocationPairRoute[]) => void;
}

const SegmentSection: React.FC<SegmentSectionProps> = ({
  index,
  segmentColor,
  route,
  control,
  watch,
  setValue,
  errors,
  tripTypeOptions,
  showFreezerIdleTime,
  watchedEnableAdditionalCost,
  canRemove,
  onRemove,
  onRouteUpdate,
}) => {
  const { t } = useAppTranslation("driverTripCalculator");

  const {
    fields: additionalCostFields,
    append: appendAdditionalCost,
    remove: removeAdditionalCost,
  } = useFieldArray({
    control,
    name: `segments.${index}.additional_costs`,
  });

  const distanceDisplay = route ? `${Math.round(route.totalDistance)} Km` : "0 Km";
  const timeDisplay = route ? formatTime(route.totalTime, t) : "0 hr";

  return (
    <div className="space-y-4" data-testid={`card-dtc-segment-${index + 1}`}>
      {/* Segment Header */}
      <div className="flex items-center justify-between">
        <Typography variant="heading3" className="text-sm font-bold underline" style={{ color: segmentColor }}>
          {t("segment-trip-planning")}
        </Typography>
        <div className="flex items-center gap-2">
          <span className="text-xl font-medium text-green-600">
            {distanceDisplay} · {timeDisplay}
          </span>
          
          {canRemove && (
            <button
              type="button"
              onClick={onRemove}
              className="rounded-full p-1 text-red-500 hover:bg-red-50 transition-colors"
              data-testid={`btn-dtc-remove-segment-${index + 1}`}
            >
              <Icon name="trash" className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Trip Type */}
      <div>
        <Controller
          name={`segments.${index}.trip_type_id`}
          control={control}
          render={({ field, fieldState }) => (
            <Select
              {...field}
              label={t("field-trip-type")}
              options={tripTypeOptions}
              value={field.value}
              onValueChange={(val) => {
                field.onChange(val);
                setValue(`segments.${index}.freezer_idle_hours`, null);
              }}
              placeholder={t("placeholder-trip-type")}
              required
              error={fieldState.error}
              data-testid={`dropdown-dtc-trip-type-${index + 1}`}
            />
          )}
        />
      </div>

      {/* Trip Locations (multi-waypoint) */}
      <TripLocationInput
        segmentIndex={index}
        control={control}
        watch={watch}
        setValue={setValue}
        onRouteUpdate={onRouteUpdate}
        pairDistances={route?.pairs}
      />

      {/* Rest Time + Freezer Idle Time row */}
      <div className="grid grid-cols-2 gap-4">
        <Controller
          name={`segments.${index}.rest_time`}
          control={control}
          render={({ field }) => (
            <div>
              <label className="mb-1 block text-sm font-medium text-text-primary">
                {t("field-rest-time")}
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={field.value ?? 0}
                  onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  min={0}
                  data-testid={`input-dtc-rest-time-${index + 1}`}
                />
                <span className="whitespace-nowrap rounded-md bg-gray-100 px-3 py-2 text-xs font-medium text-gray-600">
                  {t("unit-rest-hours")}
                </span>
              </div>
            </div>
          )}
        />

        {showFreezerIdleTime && (
          <Controller
            name={`segments.${index}.freezer_idle_hours`}
            control={control}
            render={({ field, fieldState }) => (
              <div>
                <label className="mb-1 block text-sm font-medium text-text-primary">
                  {t("field-freezer-idle-time")}
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={field.value ?? ""}
                    onChange={(e) => {
                      const v = e.target.value ? parseFloat(e.target.value) : null;
                      field.onChange(v);
                    }}
                    className={`w-full rounded-lg border px-3 py-2.5 text-sm outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500 ${
                      fieldState.error ? "border-red-500" : "border-gray-300"
                    }`}
                    min={0}
                    data-testid={`input-dtc-freezer-idle-time-${index + 1}`}
                  />
                  <span className="whitespace-nowrap rounded-md bg-gray-100 px-3 py-2 text-xs font-medium text-gray-600">
                    {t("unit-idle-hours")}
                  </span>
                </div>
                {fieldState.error && (
                  <p className="mt-1 text-xs text-red-500">{fieldState.error.message}</p>
                )}
              </div>
            )}
          />
        )}
      </div>

      {/* Enable Additional Cost Toggle */}
      <div className="flex items-center justify-between">
        <div>
          <span className="text-sm font-medium text-text-primary">{t("toggle-enable-additional-cost")}</span>
          <p className="text-xs text-gray-500">{t("toggle-enable-additional-cost-desc")}</p>
        </div>
        <Controller
          name={`segments.${index}.enable_additional_cost`}
          control={control}
          render={({ field }) => (
            <button
              type="button"
              onClick={() => field.onChange(!field.value)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                field.value ? "bg-primary-500" : "bg-gray-300"
              }`}
              data-testid={`toggle-dtc-additional-cost-${index + 1}`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  field.value ? "translate-x-6" : "translate-x-1"
                }`}
              />
            </button>
          )}
        />
      </div>

      {/* Additional Costs Section */}
      {watchedEnableAdditionalCost && (
        <div className="space-y-3">
          {additionalCostFields.map((costField, costIndex) => (
            <AdditionalCostRow
              key={costField.id}
              segmentIndex={index}
              costIndex={costIndex}
              control={control}
              onRemove={() => removeAdditionalCost(costIndex)}
              error={errors.segments?.[index]?.additional_costs?.[costIndex]}
            />
          ))}
          <button
            type="button"
            onClick={() => appendAdditionalCost({ cost_type: "", amount: "" })}
            className="flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700"
            data-testid={`btn-dtc-add-cost-${index + 1}`}
          >
            <Icon name="plus" className="h-4 w-4" />
            {t("btn-additional-cost")}
          </button>
        </div>
      )}

      {/* Divider between segments */}
      <hr className="border-gray-200" />
    </div>
  );
};

export default SegmentSection;
